/** AgingChart — bucketed bars (e.g. past-due aging profile). Bars are capped at 24px
 *  and the leftover band is air, so a five-bucket profile reads as five marks rather
 *  than five slabs; the axis carries the scale the caps do not. */
import { useState } from 'react'
import { useTokens, type Tokens } from './useTokens'
import { useChartWidth } from './useChartWidth'
import { axisScale, barPath, barWidth } from './chartMarks'
import AxisRows from './AxisRows'

export interface AgeBucket {
  label: string
  value: number
  color: string
}

const H = 210
const PAD = { l: 48, r: 16, t: 22, b: 34 }
const ROWS = 4

function Bucket({ b, cx, band, bw, y, bottom, dim, fmt, t }: {
  b: AgeBucket; cx: number; band: number; bw: number; y: (v: number) => number
  bottom: number; dim: boolean; fmt: (v: number) => string; t: Tokens
}) {
  return (
    <>
      <rect x={cx - band / 2} y={PAD.t} width={band} height={bottom - PAD.t} fill="transparent" />
      <path d={barPath(cx - bw / 2, y(b.value), bw, bottom - y(b.value))} fill={b.color} opacity={dim ? 0.45 : 0.92} />
      {/* A handful of buckets is few enough to label every cap — the flood the spec
          warns about starts well above this. */}
      <text x={cx} y={y(b.value) - 8} textAnchor="middle" fill={t.mut} fontSize={10} fontWeight={600} fontFamily="JetBrains Mono">
        {fmt(b.value)}
      </text>
      <text x={cx} y={H - 12} textAnchor="middle" fill={t.mut2} fontSize={10} fontFamily="JetBrains Mono">{b.label}</text>
    </>
  )
}

export default function AgingChart({ buckets, fmt = (v: number) => `${Math.round(v)}` }: {
  buckets: AgeBucket[]
  fmt?: (v: number) => string
}) {
  const t = useTokens()
  const [box, W] = useChartWidth()
  const [hover, setHover] = useState<number | null>(null)
  const plotH = H - PAD.t - PAD.b
  const bottom = PAD.t + plotH
  const axis = axisScale(0, Math.max(...buckets.map((b) => b.value), 0), ROWS)
  const y = (v: number): number => bottom - plotH * (v / (axis.step * ROWS || 1))
  const band = Math.max(W - PAD.l - PAD.r, 40) / Math.max(buckets.length, 1)
  const bw = barWidth(band)
  return (
    <div ref={box} className="w-full min-w-0">
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: H, display: 'block' }} role="img" aria-label="Aging profile by bucket">
        <AxisRows x1={PAD.l} x2={W - PAD.r} rows={ROWS} rowY={(r) => y(axis.step * r)} left={(r) => fmt(axis.step * r)} t={t} />
        {buckets.map((b, i) => (
          <g key={i} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
            <Bucket b={b} cx={PAD.l + band * i + band / 2} band={band} bw={bw} y={y} bottom={bottom}
              dim={hover !== null && hover !== i} fmt={fmt} t={t} />
          </g>
        ))}
      </svg>
    </div>
  )
}
